import { head, isArray, isEmpty, isString, keys } from 'lodash-es'
import { ValidationError } from 'yup'

import { EMPTY_STRING } from '../constants/common'
import {
  FORMIK_EMPTY_VALUE,
  SERVER_ERROR_CONTEXT_KEY,
  SERVER_ERROR_DETAIL_OBJECT_KEY,
  SERVER_ERROR_MESSAGE_KEY,
  SERVER_ERROR_OBJECT_KEY,
  SERVER_NON_FIELD_ERROR_KEY,
  SERVER_NON_FIELD_ERROR_OLD_API_KEY,
} from '../constants/validation'
import i18n from '../i18n'

import { isApiBadRequestStatus } from './api'

function toServerError(error) {
  return `${SERVER_ERROR_CONTEXT_KEY}.${error}`
}

export function isFormikEmptyValue(value) {
  return value === FORMIK_EMPTY_VALUE
}

export function parseServerKey(error) {
  if (!error) {
    return error
  }

  if (error.includes(SERVER_ERROR_CONTEXT_KEY)) {
    return i18n.t(error)
  }

  return error
}

export function checkForOldApiKey(key) {
  if (key === SERVER_NON_FIELD_ERROR_OLD_API_KEY) {
    return SERVER_NON_FIELD_ERROR_KEY
  }

  return key
}

export function parseFromOldApiServer(error) {
  if (!error) {
    return error
  }

  if (isString(error) && error.includes('Key')) {
    return parseServerKey(toServerError(error))
  }

  return error
}

export function parseNonFieldError(error) {
  if (error.includes(SERVER_ERROR_CONTEXT_KEY)) {
    return i18n.t(error)
  }

  return null
}

export function parseServerValidation(errors) {
  const parsed = {}

  if (isString(errors)) {
    return { [SERVER_NON_FIELD_ERROR_KEY]: parseServerKey(errors) }
  }

  if (isString(errors?.[SERVER_ERROR_OBJECT_KEY])) {
    return {
      [SERVER_NON_FIELD_ERROR_KEY]: parseServerKey(
        errors[SERVER_ERROR_OBJECT_KEY]
      ),
    }
  }

  const errorsObject = { ...errors?.[SERVER_ERROR_OBJECT_KEY] }

  keys(errorsObject).map(i => {
    isArray(errorsObject[i])
      ? (parsed[checkForOldApiKey(i)] = parseServerKey(head(errorsObject[i])))
      : (parsed[checkForOldApiKey(i)] = parseServerKey(errorsObject[i]))
  })

  if (!isEmpty(errors?.[SERVER_ERROR_DETAIL_OBJECT_KEY])) {
    parsed[SERVER_NON_FIELD_ERROR_KEY] = parseNonFieldError(
      errors[SERVER_ERROR_DETAIL_OBJECT_KEY]
    )
  }

  if (errorsObject[SERVER_NON_FIELD_ERROR_OLD_API_KEY]) {
    parsed[SERVER_NON_FIELD_ERROR_KEY] = parseFromOldApiServer(
      errorsObject[SERVER_NON_FIELD_ERROR_OLD_API_KEY]
    )
  }

  // This case appears in `Inhabitants` form. Returning without translation cause need to understand which error comes from backend.
  if (errorsObject[SERVER_ERROR_MESSAGE_KEY]) {
    parsed[SERVER_ERROR_MESSAGE_KEY] = toServerError(
      errorsObject[SERVER_ERROR_MESSAGE_KEY]
    )
  }

  return parsed
}

// Toast key for a failed request: the server's non-field error (a plain
// `errors` string, `detail`, …) when present, otherwise the action's own key.
export function getServerErrorNotificationKey(data, fallbackKey) {
  return parseServerValidation(data)[SERVER_NON_FIELD_ERROR_KEY] ?? fallbackKey
}

// Only a 400 carries the server's error key: a 5xx body is an HTML error page.
export function getBadRequestErrorNotificationKey(error, fallbackKey) {
  return isApiBadRequestStatus(error?.response?.status)
    ? getServerErrorNotificationKey(error.response.data, fallbackKey)
    : fallbackKey
}

export function excludeSpecialCharacters(value) {
  const regexString = /[^\p{Alphabetic}\d= +'\-&,._()]/giu

  return value.replace(regexString, EMPTY_STRING)
}

export function collectYupErrors(err) {
  if (!(err instanceof ValidationError)) {
    throw err
  }

  return (err.inner?.length > 0 ? err.inner : [err]).reduce((acc, item) => {
    return {
      ...acc,
      [item.path ?? '_form']: item.message,
    }
  }, {})
}
